import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import {
    ADMIN_ROLE,
    ChangePasswordRequest,
    LoginRequest,
    LoginMembership,
    LoginResponse,
    SUPER_ADMIN_ROLE,
    User,
} from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private readonly _pendingMembershipStorageKey = 'pendingMembershipSelection';
    private readonly _adminMembershipsStorageKey = 'adminMemberships';

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get pendingMembershipSelection(): PendingMembershipSelection | null {
        const raw = localStorage.getItem(this._pendingMembershipStorageKey);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as PendingMembershipSelection;
        } catch {
            localStorage.removeItem(this._pendingMembershipStorageKey);
            return null;
        }
    }

    get adminMemberships(): LoginMembership[] {
        const raw = localStorage.getItem(this._adminMembershipsStorageKey);
        if (!raw) {
            return [];
        }

        try {
            return JSON.parse(raw) as LoginMembership[];
        } catch {
            localStorage.removeItem(this._adminMembershipsStorageKey);
            return [];
        }
    }

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/forgot-password`,
            { email }
        );
    }

    resetPassword(password: string): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/reset-password`,
            { password }
        );
    }

    changePassword(request: ChangePasswordRequest): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/change-password`,
            request
        );
    }

    signIn(credentials: LoginRequest): Observable<LoginResponse> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient
            .post<LoginResponse>(
                `${environment.apiBaseUrl}/auth/login`,
                credentials
            )
            .pipe(
                switchMap((response: LoginResponse) => {
                    // Allow Admin (society admin) and SuperAdmin (platform) roles.
                    if (
                        response.roleName !== ADMIN_ROLE &&
                        response.roleName !== SUPER_ADMIN_ROLE
                    ) {
                        return throwError(
                            () =>
                                new Error(
                                    'Access denied. Only Admin users can access this panel.'
                                )
                        );
                    }

                    if (this._requiresMembershipSelection(response)) {
                        this._startMembershipSelection(response);
                    } else {
                        this._applyAuthenticatedResponse(response);
                    }

                    return of(response);
                })
            );
    }

    /**
     * Activates another account context for the current session. The API
     * returns a new access token and effective permissions for that context.
     */
    selectMembership(membershipId: number): Observable<LoginResponse> {
        const user = this._userService.user;
        const pending = this.pendingMembershipSelection;
        const userId = user?.id ?? pending?.userId;
        const sessionId = user?.sessionId ?? pending?.sessionId;
        if (!userId || !sessionId) {
            return throwError(() => new Error('Account switching is no longer available. Please sign in again.'));
        }

        return this._httpClient
            .post<LoginResponse>(`${environment.apiBaseUrl}/auth/select-membership`, {
                userId,
                sessionId,
                membershipId,
            })
            .pipe(
                switchMap((response) => {
                    if (
                        response.roleName !== ADMIN_ROLE &&
                        response.roleName !== SUPER_ADMIN_ROLE
                    ) {
                        return throwError(
                            () => new Error('This account cannot access the Admin Panel.')
                        );
                    }

                    this._applyAuthenticatedResponse(response);
                    localStorage.removeItem(this._pendingMembershipStorageKey);
                    return of(response);
                })
            );
    }

    private _applyAuthenticatedResponse(response: LoginResponse): void {
        this.accessToken = response.token;
        this._authenticated = true;

        const activeMembership = response.activeMembership ?? null;
        const memberships = response.memberships ?? [];
        const user: User = {
            id: response.userId,
            name: response.fullName,
            email: response.email,
            societyId: response.societyId ?? activeMembership?.societyId ?? null,
            societyName: response.societyName ?? activeMembership?.societyName,
            roleName: response.roleName,
            flatId: response.flatId ?? activeMembership?.flatId ?? undefined,
            flatNumber: response.flatNumber ?? activeMembership?.flatNumber ?? undefined,
            flatOwnerId: response.flatOwnerId,
            wingId: response.wingId ?? activeMembership?.wingId ?? undefined,
            wingName: response.wingName ?? activeMembership?.wingName ?? undefined,
            canManageCommonAccount:
                response.canManageCommonAccount ?? activeMembership?.canManageCommonAccount ?? false,
            membershipId: activeMembership?.membershipId,
            sessionId: response.sessionId,
            memberships,
        };
        this._userService.user = user;
        localStorage.setItem('userData', JSON.stringify(user));
    }

    signInUsingToken(): Observable<boolean> {
        // Validate token is not expired
        if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // A pending choice is authenticated enough to call select-membership,
        // but no society context is active until the choice is submitted.
        if (this.pendingMembershipSelection) {
            this._authenticated = true;
            return of(true);
        }

        // Restore user from localStorage
        const storedUser = localStorage.getItem('userData');
        if (!storedUser) {
            return of(false);
        }

        try {
            const user: User = JSON.parse(storedUser);
            // Allow Admin and SuperAdmin roles.
            if (
                user.roleName !== ADMIN_ROLE &&
                user.roleName !== SUPER_ADMIN_ROLE
            ) {
                this.signOut();
                return of(false);
            }
            this._authenticated = true;
            this._userService.user = user;
            return of(true);
        } catch {
            return of(false);
        }
    }

    signOut(): Observable<any> {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userData');
        localStorage.removeItem(this._pendingMembershipStorageKey);
        localStorage.removeItem(this._adminMembershipsStorageKey);
        // Clear any SuperAdmin impersonation backup so a fresh login starts clean.
        localStorage.removeItem('superAdminToken');
        localStorage.removeItem('superAdminUserData');
        this._authenticated = false;
        return of(true);
    }

    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken) {
            return of(false);
        }

        if (AuthUtils.isTokenExpired(this.accessToken)) {
            this.signOut();
            return of(false);
        }

        return this.signInUsingToken();
    }

    private _startMembershipSelection(response: LoginResponse): void {
        const memberships = this._eligibleAdminMemberships(response.memberships ?? []);
        if (!response.sessionId || memberships.length < 2) {
            throw new Error('No eligible Admin society accounts are available.');
        }

        // Keep the session details in memory so selectMembership can activate
        // the chosen account from the membership-selection page.
        this._applyAuthenticatedResponse(response);
        this._storeAdminMemberships(memberships);
        localStorage.setItem(
            this._pendingMembershipStorageKey,
            JSON.stringify({
                userId: response.userId,
                fullName: response.fullName,
                sessionId: response.sessionId,
                memberships,
                activeMembershipId: response.activeMembership?.membershipId ?? memberships[0].membershipId,
            } satisfies PendingMembershipSelection)
        );
    }

    private _storeAdminMemberships(memberships: LoginMembership[]): void {
        localStorage.setItem(
            this._adminMembershipsStorageKey,
            JSON.stringify(
                memberships.filter(
                    (membership) => membership.roleName === ADMIN_ROLE && membership.isActive
                )
            )
        );
    }

    private _requiresMembershipSelection(response: LoginResponse): boolean {
        return response.membershipSelectionRequired
            || this._eligibleAdminMemberships(response.memberships ?? []).length > 1;
    }

    private _eligibleAdminMemberships(memberships: LoginMembership[]): LoginMembership[] {
        return memberships.filter(
            (membership) => membership.roleName === ADMIN_ROLE && membership.isActive
        );
    }
}

export interface PendingMembershipSelection {
    userId: number;
    fullName: string;
    sessionId: string;
    memberships: LoginMembership[];
    activeMembershipId: number;
}
